import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FlaskConical, Lightbulb, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { Empty, StatusBadge, FadeIn } from "@/components/Shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DIFF_COLOR = { Beginner: "bg-teal-100 text-teal-800", Intermediate: "bg-secondary/40 text-secondary-foreground", Advanced: "bg-accent/15 text-accent" };

function ProjectsContent() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [domain, setDomain] = useState("");
    const [difficulty, setDifficulty] = useState("");

    useEffect(() => {
        const q = new URLSearchParams();
        if (domain) q.set("domain", domain);
        if (difficulty) q.set("difficulty", difficulty);
        api.get(`/projects?${q.toString()}`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }, [domain, difficulty]);

    useEffect(() => {
        if (user?.role === "student") api.get("/proposals").then((r) => setProposals(r.data)).catch(() => {});
    }, [user]);

    const domains = [...new Set((projects || []).map((p) => p.domain))];

    return (
        <div className="mx-auto max-w-7xl" data-testid="projects-page">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Research & Projects</h1>
                    <p className="mt-2 text-base text-muted-foreground">Join a featured project — or propose your own idea and we'll match you with a mentor.</p>
                </div>
                <Button data-testid="propose-own-project-button" className="rounded-full" onClick={() => navigate(user ? "/propose-project" : "/login")}>
                    <Lightbulb className="mr-2 h-4 w-4" /> Propose Your Own Project
                </Button>
            </div>

            <div className="card-soft mt-8 flex flex-wrap items-end gap-5 p-6">
                <div className="space-y-2">
                    <Label>Domain</Label>
                    <Select value={domain || "all"} onValueChange={(v) => setDomain(v === "all" ? "" : v)}>
                        <SelectTrigger data-testid="project-filter-domain" className="w-48 rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All domains</SelectItem>
                            {domains.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Difficulty</Label>
                    <Select value={difficulty || "all"} onValueChange={(v) => setDifficulty(v === "all" ? "" : v)}>
                        <SelectTrigger data-testid="project-filter-difficulty" className="w-48 rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All levels</SelectItem>
                            {["Beginner", "Intermediate", "Advanced"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {projects === null ? <p className="mt-8 text-sm text-muted-foreground">Loading projects...</p>
            : projects.length === 0 ? <div className="mt-8"><Empty icon={FlaskConical} title="No projects found" hint="Try different filters — or propose your own!" /></div>
            : (
                <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {projects.map((p, i) => (
                        <FadeIn key={p.id} delay={i * 0.05}>
                            <div className="card-soft flex h-full flex-col p-7" data-testid={`project-card-${p.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{p.domain}</span>
                                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${DIFF_COLOR[p.difficulty] || "bg-muted"}`}>{p.difficulty}</span>
                                </div>
                                <h3 className="mt-4 font-display text-lg font-semibold leading-snug">{p.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.description}</p>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {p.skills?.map((s) => <span key={s} className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">{s}</span>)}
                                </div>
                                <div className="mt-auto flex items-center justify-between border-t border-border/40 pt-4 text-xs text-muted-foreground">
                                    <span>{p.duration}</span><span>Mentor: {p.posted_by_name}</span>
                                </div>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            )}

            {user?.role === "student" && (
                <div className="mt-16" data-testid="my-proposals-section">
                    <h2 className="font-display text-2xl font-semibold">My Project Proposals</h2>
                    {proposals.length === 0 ? (
                        <p className="mt-4 text-sm text-muted-foreground">You haven't proposed a project yet. <Link to="/propose-project" className="font-semibold text-primary">Have an idea? <ArrowRight className="inline h-3.5 w-3.5" /></Link></p>
                    ) : (
                        <div className="mt-6 grid gap-5 md:grid-cols-2">
                            {proposals.map((p) => (
                                <div key={p.id} className="card-soft p-6" data-testid={`proposal-${p.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-display text-base font-semibold">{p.title}</p>
                                            <p className="mt-1 text-xs text-muted-foreground">{p.domain} · {p.duration}</p>
                                        </div>
                                        <StatusBadge status={p.status} />
                                    </div>
                                    <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{p.idea}</p>
                                    <p className="mt-4 rounded-lg bg-muted px-4 py-2.5 text-xs text-muted-foreground">Stage: <span className="font-semibold text-foreground">{p.stage}</span></p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function Projects() {
    const { user } = useAuth();
    if (user) return <DashboardLayout title="Research & Projects"><ProjectsContent /></DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14"><ProjectsContent /></div>
            <Footer />
        </div>
    );
}
